import { Component, OnInit } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { TranslocoService } from '@jsverse/transloco';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { ActionPlanInfo } from 'app/modules/widget/services/action-plan-ipd.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  ActionItem,
  ActionPlanService,
  ActionTask,
  Status,
} from 'app/providers/storage-api/actionplan.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { ActionPlanUtils } from 'app/utils/action-plan.utils';
import { ActionItemListFilter } from '../action-item-list-filter/action-item-list-filter.component';
import {
  ActionPlanDetailsTableTabBase,
  IDataItem,
  IDataItemDefaults,
} from '../../shared/action-plan-details-table-tab-base';

interface ItemDataItem extends IDataItem {
  tasks: IDataItemDefaults[];
}

@EnableClassTelemetry()
@Component({
  selector: 'app-action-plan-details-items-tab',
  templateUrl: './action-plan-details-items-tab.component.html',
  styleUrls: ['./action-plan-details-items-tab.component.scss'],
  standalone: false,
})
export class ActionPlanDetailsItemsTabComponent
  extends ActionPlanDetailsTableTabBase<ItemDataItem>
  implements OnInit
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  displayedColumns: string[] = ['icon', 'name', 'due_date', 'status', 'assignee', 'actions'];

  isActionItemDialogOpen = false;
  isActionTaskDialogOpen = false;
  actionPlanInfo: ActionPlanInfo;
  itemOnMenu: ItemDataItem;
  taskOnMenu: IDataItemDefaults;
  actionItemToDialog: ActionItem;
  actionTaskToDialog: ActionTask;

  constructor(
    authService: AuthService,
    private actionPlanService: ActionPlanService,
    private alertService: AlertService,
    private dialogService: DialogService,
    private translocoService: TranslocoService
  ) {
    super(authService);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.actionPlanInfo = {
      actionPlan: this.actionPlan,
    };
  }

  onToggleAllRow(): void {
    this.toggleAllRow();
  }

  onToggleRow(item: ItemDataItem): void {
    this.toggleRow(item);
  }

  onSortChange(sort: Sort): void {
    this.sortChange(sort);
  }

  onSearch(value: string): void {
    this.search(value);
  }

  onOpenFilterModal(): void {
    this.openFilterModal();
  }

  onCloseFilterModal(filter: ActionItemListFilter | null): void {
    this.closeFilterModal(filter);
  }

  onOpenItemActionsMenu(item: ItemDataItem): void {
    this.itemOnMenu = item;
  }

  onOpenTaskActionsMenu(item: ItemDataItem, task: IDataItemDefaults): void {
    this.itemOnMenu = item;
    this.taskOnMenu = task;
  }

  async onItemStatusChange(status: Status, item: ItemDataItem): Promise<void> {
    const originalStatus = item.status;

    const actionItem = this.actionPlan.action_items.find((i) => i.item_id === item.id);
    actionItem.status = status;

    try {
      await this.actionPlanService.updateActionItem(this.actionPlan.plan_id, actionItem);

      ActionPlanUtils.setChildCountsForPlan(this.actionPlan);

      item.status = status;

      item.isExpired = ActionPlanUtils.isExpired(actionItem);
      item.expiredTooltip = ActionPlanUtils.getExpiredLabel(actionItem);

      this.setDataSource();

      this.alertService.showToast(
        this.translocoService.translateObject('action-plan-details-items-tab.status-update-success')
      );
    } catch (error) {
      actionItem.status = originalStatus;

      this.alertService.showErrorToast(
        this.translocoService.translateObject(
          'action-plan-details-items-tab.status-update-failure'
        ),
        error
      );
    }
  }

  async onTaskStatusChange(status: Status, itemId: string, task: IDataItemDefaults): Promise<void> {
    const originalStatus = task.status;

    const actionItem = this.actionPlan.action_items.find((i) => i.item_id === itemId);
    const actionTask = actionItem.action_tasks.find((t) => t.task_id === task.id);
    actionTask.status = status;

    try {
      await this.actionPlanService.updateActionTask(
        this.actionPlan.plan_id,
        actionItem.item_id,
        actionTask
      );

      ActionPlanUtils.setChildCountsForItem(actionItem);

      task.status = status;

      task.isExpired = ActionPlanUtils.isExpired(actionTask);
      task.expiredTooltip = ActionPlanUtils.getExpiredLabel(actionTask);

      if (this.sort.active === 'status') {
        const item = this.dataList.find((i) => i.id === itemId);
        item.tasks = [...item.tasks].sort(this.sorting);
      }

      this.alertService.showToast(
        this.translocoService.translateObject('action-plan-details-items-tab.status-update-success')
      );
    } catch (error) {
      actionTask.status = originalStatus;

      this.alertService.showErrorToast(
        this.translocoService.translateObject(
          'action-plan-details-items-tab.status-update-failure'
        ),
        error
      );
    }
  }

  onAddTask(): void {
    this.actionItemToDialog = this.actionPlan.action_items.find(
      (i) => i.item_id === this.itemOnMenu.id
    );

    this.actionTaskToDialog = undefined;

    this.isActionTaskDialogOpen = true;
  }

  async onDeleteItem(): Promise<void> {
    this.dialogService
      .showWarningPopup({
        title: this.translocoService.translate(
          'action-plan-details-items-tab.delete-item-confirmation-title',
          { name: this.itemOnMenu.name }
        ),
        content: this.translocoService.translate(
          'action-plan-details-items-tab.delete-item-confirmation-content'
        ),
        confirmTitle: this.translocoService.translate(
          'action-plan-details-items-tab.delete-action-item'
        ),
        cancelTitle: this.translocoService.translate(
          'action-plan-details-items-tab.delete-task-confirmation-cancel'
        ),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          try {
            await this.actionPlanService.deleteActionItem(
              this.actionPlan.plan_id,
              this.itemOnMenu.id
            );

            const actionItemIndex = this.actionPlan.action_items.findIndex(
              (i) => i.item_id === this.itemOnMenu.id
            );
            this.actionPlan.action_items.splice(actionItemIndex, 1);

            ActionPlanUtils.setChildCountsForPlan(this.actionPlan);

            const itemIndex = this.dataList.indexOf(this.itemOnMenu);
            this.dataList.splice(itemIndex, 1);

            this.setDataSource();

            this.countChange.emit();

            this.alertService.showToast(
              this.translocoService.translateObject(
                'action-plan-details-items-tab.delete-item-success'
              )
            );
          } catch (error) {
            this.alertService.showErrorToast(
              this.translocoService.translateObject(
                'action-plan-details-items-tab.delete-item-failure'
              ),
              error
            );
          }
        }
      });
  }

  onEditItem(): void {
    this.actionItemToDialog = this.actionPlan.action_items.find(
      (i) => i.item_id === this.itemOnMenu.id
    );

    this.isActionItemDialogOpen = true;
  }

  onUpdateActionItem(actionItem: ActionItem): void {
    const actionItemIndex = this.actionPlan.action_items.indexOf(this.actionItemToDialog);
    this.actionPlan.action_items[actionItemIndex] = actionItem;

    ActionPlanUtils.setChildCountsForPlan(this.actionPlan);

    const item = this.dataList.find((i) => i.id === actionItem.item_id);
    const itemIndex = this.dataList.indexOf(item);
    this.dataList[itemIndex] = {
      ...item,
      name: actionItem.name,
      dueDate: new Date(actionItem.due_date),
      assignee: actionItem.assignee_user_name,
      notes: actionItem.notes,
      isExpired: ActionPlanUtils.isExpired(actionItem),
      expiredTooltip: ActionPlanUtils.getExpiredLabel(actionItem),
    };

    this.setDataSource();
  }

  onCloseActionItemDialog(): void {
    this.isActionItemDialogOpen = false;
  }

  onEditTask(): void {
    const actionItem = this.actionPlan.action_items.find((i) => i.item_id === this.itemOnMenu.id);

    this.actionItemToDialog = actionItem;
    this.actionTaskToDialog = actionItem.action_tasks.find((t) => t.task_id === this.taskOnMenu.id);

    this.isActionTaskDialogOpen = true;
  }

  async onDeleteTask(): Promise<void> {
    this.dialogService
      .showWarningPopup({
        title: this.translocoService.translate(
          'action-plan-details-items-tab.delete-task-confirmation-title',
          { name: this.taskOnMenu.name }
        ),
        content: this.translocoService.translate(
          'action-plan-details-items-tab.delete-task-confirmation-content'
        ),
        confirmTitle: this.translocoService.translate('action-plan-details-items-tab.delete-task'),
        cancelTitle: this.translocoService.translate(
          'action-plan-details-items-tab.delete-task-confirmation-cancel'
        ),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          try {
            await this.actionPlanService.deleteActionTask(
              this.actionPlan.plan_id,
              this.itemOnMenu.id,
              this.taskOnMenu.id
            );

            const actionItem = this.actionPlan.action_items.find(
              (i) => i.item_id === this.itemOnMenu.id
            );
            const actionTaskIndex = actionItem.action_tasks.findIndex(
              (t) => t.task_id === this.taskOnMenu.id
            );
            actionItem.action_tasks.splice(actionTaskIndex, 1);

            ActionPlanUtils.setChildCountsForItem(actionItem);

            const item = this.dataList.find((i) => i.id === this.itemOnMenu.id);
            const taskIndex = item.tasks.indexOf(this.taskOnMenu);
            item.tasks.splice(taskIndex, 1);

            item.tasks = [...item.tasks];

            this.countChange.emit();

            this.alertService.showToast(
              this.translocoService.translateObject(
                'action-plan-details-items-tab.delete-task-success'
              )
            );
          } catch (error) {
            this.alertService.showErrorToast(
              this.translocoService.translateObject(
                'action-plan-details-items-tab.delete-task-failure'
              ),
              error
            );
          }
        }
      });
  }

  onCreateActionTask(actionTask: ActionTask): void {
    const actionItem = this.actionPlan.action_items.find(
      (i) => i.item_id === this.actionItemToDialog.item_id
    );
    if (actionItem.action_tasks) {
      actionItem.action_tasks.push(actionTask);
    } else {
      actionItem.action_tasks = [actionTask];
    }

    ActionPlanUtils.setChildCountsForItem(actionItem);

    this.itemOnMenu.tasks = [...this.itemOnMenu.tasks, this.mapActionTask(actionTask)].sort(
      this.sorting
    );

    this.countChange.emit();
  }

  onUpdateActionTask(actionTask: ActionTask): void {
    const actionItem = this.actionPlan.action_items.find(
      (i) => i.item_id === this.actionItemToDialog.item_id
    );
    const actionTaskIndex = actionItem.action_tasks.findIndex(
      (t) => t.task_id === actionTask.task_id
    );
    actionItem.action_tasks[actionTaskIndex] = actionTask;

    ActionPlanUtils.setChildCountsForItem(actionItem);

    const taskIndex = this.itemOnMenu.tasks.indexOf(this.taskOnMenu);
    this.itemOnMenu.tasks[taskIndex] = this.mapActionTask(actionTask);

    this.itemOnMenu.tasks = [...this.itemOnMenu.tasks].sort(this.sorting);
  }

  onCloseActionTaskDialog(): void {
    this.isActionTaskDialogOpen = false;
  }

  protected getDataList(): ItemDataItem[] {
    return (this.actionPlan.action_items ?? []).map((i) => this.mapActionItem(i));
  }

  protected sorting = (a: ItemDataItem, b: ItemDataItem): number => {
    let result = 0;
    switch (this.sort.active) {
      case 'name':
        result = a.name.localeCompare(b.name);
        break;
      case 'due_date':
        result = a.dueDate.getTime() - b.dueDate.getTime();
        break;
      case 'status':
        result = this.getStatusValue(a) - this.getStatusValue(b);
        break;
      case 'assignee':
        // optional field, empties are last
        if (!a.assignee) {
          result = 1;
        } else if (!b.assignee) {
          result = -1;
        } else {
          result = a.assignee.localeCompare(b.assignee);
        }
        break;
    }

    const direction = this.sort.direction === 'asc' ? 1 : -1;
    return result * direction;
  };

  protected applySorting(items: ItemDataItem[]): ItemDataItem[] {
    items = super.applySorting(items);

    items.forEach((i) => (i.tasks = [...i.tasks].sort(this.sorting)));

    return items;
  }

  private mapActionItem(actionItem: ActionItem): ItemDataItem {
    const actionTasks = actionItem.action_tasks?.map((t) => this.mapActionTask(t)) ?? [];

    return {
      id: actionItem.item_id,
      name: actionItem.name,
      dueDate: new Date(actionItem.due_date),
      status: actionItem.status,
      assignee: actionItem.assignee_user_name,
      notes: actionItem.notes,
      isExpired: ActionPlanUtils.isExpired(actionItem),
      expiredTooltip: ActionPlanUtils.getExpiredLabel(actionItem),
      tasks: actionTasks,
      isExpanded: false,
    };
  }

  private mapActionTask(actionTask: ActionTask): IDataItemDefaults {
    return {
      id: actionTask.task_id,
      name: actionTask.name,
      dueDate: new Date(actionTask.due_date),
      status: actionTask.status,
      assignee: actionTask.assignee_user_name,
      isExpired: ActionPlanUtils.isExpired(actionTask),
      expiredTooltip: ActionPlanUtils.getExpiredLabel(actionTask),
    };
  }
}
